import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { handleError } from "@/api/error";
import type { Pagination } from "@/api/pagination";
import { toQuery } from "@/api/pagination";
import { useMerchant } from "@/providers/MerchantProvider";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export const useMessageTemplates = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["message-templates", scope.merchantId, scope.workspaceId],
    queryFn: async () => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["message-templates"].$get({
            param: scope.param,
            query: {},
          })
        : await client.merchant[":merchantId"]["message-templates"].$get({
            param: scope.param,
            query: {},
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

export type MessageTemplate = NonNullable<ReturnType<typeof useMessageTemplates>["data"]>[number];

export const useMessageTemplateCategories = () => {
  const { data: templates = [] } = useMessageTemplates();
  return useMemo(() => Array.from(new Set(templates.map((t) => t.category))), [templates]);
};

type TemplatesListOptions = {
  pagination: Pagination;
  search?: string;
  sort?: { column: string; direction: "asc" | "desc" };
};

export const usePaginatedMessageTemplates = (options: TemplatesListOptions) => {
  const merchantId = useMerchant();
  const { pagination, search, sort } = options;

  return useQuery({
    queryKey: [
      "message-templates",
      merchantId,
      "paginated",
      pagination.offset,
      pagination.limit,
      search,
      sort?.column,
      sort?.direction,
    ],
    queryFn: async () => {
      if (!merchantId) throw new Error("No merchant selected");

      const res = await client.merchant[":merchantId"]["message-templates"].$get({
        param: { merchantId },
        query: {
          ...toQuery(pagination),
          ...(search ? { search } : {}),
          ...(sort?.column
            ? { column: sort.column as "name" | "category" | "createdAt", direction: sort.direction }
            : {}),
        },
      });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!merchantId,
  });
};

export const useMessageTemplatesCount = (opts?: { search?: string }) => {
  const merchantId = useMerchant();
  const search = opts?.search;

  return useQuery({
    queryKey: ["message-templates-count", merchantId, search],
    queryFn: async () => {
      if (!merchantId) throw new Error("No merchant selected");

      const res = await client.merchant[":merchantId"]["message-templates"].count.$get({
        param: { merchantId },
        query: {
          ...(search ? { search } : {}),
        },
      });

      if (!res.ok) throw await handleError(res);
      return (await res.json()).total;
    },
    enabled: !!merchantId,
  });
};

export const useCreateMessageTemplate = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { name: string; category: string; content: string; imageId?: string | null }) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = await client.merchant[":merchantId"]["message-templates"].$post({
        param: { merchantId: scope.merchantId },
        json: input,
      });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["message-templates", scope.merchantId] });
      queryClient.invalidateQueries({ queryKey: ["message-templates-count", scope.merchantId] });
    },
  });
};

export const useUpdateMessageTemplate = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: { name?: string; category?: string; content?: string; imageId?: string | null };
    }) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = await client.merchant[":merchantId"]["message-templates"][":id"].$put({
        param: { merchantId: scope.merchantId, id },
        json: input,
      });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["message-templates", scope.merchantId] });
      queryClient.invalidateQueries({ queryKey: ["message-templates-count", scope.merchantId] });
    },
  });
};

export const useDeleteMessageTemplate = () => {
  const merchantId = useMerchant();
  const queryClient = useQueryClient();

  return useMutation({
    meta: { suppressGlobalError: true },
    mutationFn: async ({ id, force = false }: { id: string; force?: boolean }) => {
      if (!merchantId) throw new Error("No merchant selected");

      const res = await client.merchant[":merchantId"]["message-templates"][":id"].$delete({
        param: { merchantId, id },
        query: { force: String(force) as "true" | "false" },
      });

      if (!res.ok) throw await handleError(res);
      return (await res.json()) as { deleted: boolean; inUse?: { sequence: boolean } };
    },
    onSuccess: (data) => {
      if (data.deleted) {
        queryClient.invalidateQueries({ queryKey: ["message-templates", merchantId] });
        queryClient.invalidateQueries({ queryKey: ["message-templates-count", merchantId] });
      }
    },
  });
};
